import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, CheckCircle2, MessageSquare, Truck, Sparkles } from 'lucide-react';
import { rateJob, JobRecord } from '../lib/activityStore';
const TAGS = ['On time', 'Professional', 'Friendly', 'Careful with items', 'Good communication', 'Clean truck', 'Would book again'];
interface Props {
  job: JobRecord;
  onClose: () => void;
}
export function RateJobModal({
  job,
  onClose
}: Props) {
  const [hover, setHover] = useState(0);
  const [stars, setStars] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const toggleTag = (t: string) => {
    setTags(arr => arr.includes(t) ? arr.filter(x => x !== t) : [...arr, t]);
  };
  const handleSubmit = () => {
    if (!stars) return;
    rateJob(job.id, stars, review, tags);
    setSubmitted(true);
    setTimeout(onClose, 1400);
  };
  const ratingLabel = ['Tap to rate', 'Poor', 'Fair', 'Good', 'Great', 'Outstanding'];
  return <AnimatePresence data-id="element-379">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-380">
        <motion.div initial={{
        y: 30,
        scale: 0.98
      }} animate={{
        y: 0,
        scale: 1
      }} exit={{
        y: 30,
        scale: 0.98
      }} onClick={e => e.stopPropagation()} className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-md w-full overflow-hidden" data-id="element-381">
          {submitted ? <div className="p-8 text-center" data-id="element-382">
              <motion.div initial={{
            scale: 0.5,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} className="w-16 h-16 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4" data-id="element-383">
                <CheckCircle2 className="w-8 h-8" data-id="element-384" />
              </motion.div>
              <h3 className="text-xl font-display font-bold text-gray-900 mb-1" data-id="element-385">
                Thanks for rating!
              </h3>
              <p className="text-sm text-gray-500" data-id="element-386">
                Your feedback helps other movers find great carriers.
              </p>
            </div> : <>
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-amber-50/60 to-white" data-id="element-387">
                <div className="flex items-center gap-3 min-w-0" data-id="element-388">
                  <div className="w-10 h-10 rounded-2xl bg-amber-400 text-white flex items-center justify-center shrink-0" data-id="element-389">
                    <Star className="w-5 h-5 fill-current" data-id="element-390" />
                  </div>
                  <div className="min-w-0" data-id="element-391">
                    <h3 className="font-bold text-gray-900" data-id="element-392">
                      How was your move?
                    </h3>
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1" data-id="element-393">
                      <Truck className="w-3 h-3" data-id="element-394" />
                      {job.supplierName}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500" aria-label="Close" data-id="element-395">
                  <X className="w-4 h-4" data-id="element-396" />
                </button>
              </div>

              <div className="p-6" data-id="element-397">
                {/* Stars */}
                <div className="flex flex-col items-center mb-6" data-id="element-398">
                  <div className="flex gap-1.5 mb-2" data-id="element-399">
                    {[1, 2, 3, 4, 5].map(n => {
                  const active = n <= (hover || stars);
                  return <button key={n} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setStars(n)} className="p-1 transition-transform hover:scale-110" aria-label={`${n} star${n > 1 ? 's' : ''}`} data-id="element-400">
                          <Star className={`w-10 h-10 transition-colors ${active ? 'text-amber-400 fill-amber-400 drop-shadow' : 'text-gray-200 fill-gray-200'}`} strokeWidth={1.5} data-id="element-401" />
                        </button>;
                })}
                  </div>
                  <p className={`text-sm font-bold uppercase tracking-wider ${hover || stars ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-402">
                    {ratingLabel[hover || stars]}
                  </p>
                </div>

                {/* Tag chips */}
                <div className="mb-5" data-id="element-403">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2" data-id="element-404">
                    What stood out?
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-405">
                    {TAGS.map(t => {
                  const active = tags.includes(t);
                  return <button key={t} onClick={() => toggleTag(t)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${active ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-100 bg-white text-gray-600 hover:border-gray-200'}`} data-id="element-406">
                          {t}
                        </button>;
                })}
                  </div>
                </div>

                {/* Review text */}
                <div className="mb-5" data-id="element-407">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1" data-id="element-408">
                    <MessageSquare className="w-3 h-3" data-id="element-409" />
                    Add a review{' '}
                    <span className="font-normal normal-case text-gray-400" data-id="element-410">
                      (optional)
                    </span>
                  </label>
                  <textarea value={review} onChange={e => setReview(e.target.value)} rows={3} placeholder="Share details so others know what to expect…" className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm focus:border-brand-300 resize-none" data-id="element-411" />
                </div>

                <div className="flex gap-3" data-id="element-412">
                  <button onClick={onClose} className="flex-1 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold text-sm transition-colors" data-id="element-413">
                    Skip
                  </button>
                  <button onClick={handleSubmit} disabled={!stars} className="flex-1 py-3 bg-gray-900 hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2" data-id="element-414">
                    <Sparkles className="w-4 h-4" data-id="element-415" />
                    Submit rating
                  </button>
                </div>
              </div>
            </>}
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}